import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../../components/BackButton';
import {
  Clock,
  Upload,
  LayoutDashboard,
  GanttChart,
  Unlock,
  Loader2,
  Trash2,
  FileSpreadsheet,
  Pencil,
  History,
  RotateCcw,
  Download,
  AlertTriangle,
  MoreVertical,
  CheckCircle,
  Plus,
  SmilePlus,
  ChevronUp,
  ChevronDown,
  SeparatorHorizontal,
  ArrowUp,
  ArrowUpToLine,
  Users,
} from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import Modal from '../../../components/Modal';
import {
  uploadTemplatesImportFile,
  getFileKindForTab,
  getFileKindFromRow,
  listImportsByTab,
  subscribeBootcampUnlockLeads,
  subscribeBootcampUnlockLines,
  addBootcampUnlockLine,
  updateBootcampUnlockLine,
  deleteBootcampUnlockLine,
  deleteTemplatesImport,
  deleteBootcampUnlockLead,
  getBootcampUnlockEnabledPublic,
  setBootcampUnlockEnabled,
  updateTemplatesImportMetadata,
  setTemplatesImportBootcampPublished,
  updateTemplatesImportEmoji,
  getImportGroupEmojis,
  saveImportGroupEmoji,
  uploadTemplatesImportVersion,
  fetchImportVersionHistory,
  rollbackImportToVersion,
  saveImportsOrder,
} from '../../../services/templatesImportsService';
import { suggestNextVersion } from '../../../services/hrCollectionsService';
import PortalBackground from '../../../components/PortalBackground';
import BBALoader from '../../../components/BBALoader';
import CrmStatCard from '../../../components/crm/CrmStatCard';
// Lazy-loaded so the full emoji picker (~large bundle) only downloads when opened.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

const TABS = [
  {
    id: 'cost-groups',
    label: 'Cost Groups',
    emoji: '💰',
    blurb: 'CSV templates for grouped cost tracking and management.',
  },
  {
    id: 'schedules',
    label: 'Schedules',
    emoji: '📅',
    blurb: 'CSV templates for timelines and milestones.',
  },
  {
    id: 'dashboards',
    label: 'Dashboards',
    emoji: '📊',
    blurb: 'JSON files for KPI and reporting dashboard templates.',
  },
  {
    id: 'todos',
    label: "To-Do's",
    emoji: '✅',
    blurb: 'CSV templates for to-do and checklist imports.',
  },
  {
    id: 'workflows',
    label: 'Workflows',
    emoji: '🔁',
    blurb: 'JSON files for workflow definitions and automation handoffs.',
  },
  {
    id: 'unlock-info',
    label: 'Unlock info',
    emoji: '🔓',
    blurb:
      'Submissions from the JobTread Bootcamp unlock form (name, email, phone), newest first. Draw an event line to split them by event.',
  },
];

const DEPARTMENTS = [
  { id: 'general', name: 'General' },
  { id: 'sales', name: 'Sales' },
  { id: 'operations', name: 'Operations' },
  { id: 'finance', name: 'Finance' },
  { id: 'technology', name: 'Technology' },
  { id: 'marketing', name: 'Marketing' },
  { id: 'hr', name: 'HR' },
];

function formatCreatedAt(ts) {
  if (!ts) return '';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function tsMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  const ms = new Date(ts).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

function formatDateTime(ts) {
  const ms = tsMs(ts);
  if (!ms) return '';
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** `<input type="datetime-local">` value (local time, minute precision) for a millis time. */
function toLocalInputValue(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

/**
 * Interleave event lines into the newest-first unlock submissions. A line sits
 * at its `at` time: submissions created strictly after it render above it, so
 * each line's `count` is the submissions between it and the next newer line.
 */
function buildUnlockTimeline(rows, linesNewestFirst) {
  const items = [];
  let li = 0;
  let count = 0;
  const flushLinesFrom = (t) => {
    while (li < linesNewestFirst.length && tsMs(linesNewestFirst[li].at) >= t) {
      items.push({ kind: 'line', line: linesNewestFirst[li], count });
      count = 0;
      li += 1;
    }
  };
  rows.forEach((row) => {
    flushLinesFrom(tsMs(row.createdAt));
    items.push({ kind: 'row', row });
    count += 1;
  });
  flushLinesFrom(-Infinity);
  return items;
}

async function downloadRemoteFile(url, filename) {
  const safeName = filename?.trim() || 'download';
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = safeName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

/**
 * CRM manager for JobTread import files. Mirrors the Sales/HR collections look:
 * pill group tabs, two-column cards with an "Import file" panel (new version /
 * download / history) and a "Bootcamp page" publish panel, kebab menu for edit
 * and delete, and the shared emoji picker for groups and imports.
 */
const TemplatesImports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  // CRM sidebar search deep-links land here as ?type=<tabId>&highlight=<id> —
  // start on the matching tab, then briefly highlight the linked import card.
  const [activeTab, setActiveTab] = useState(() => {
    const type = new URLSearchParams(window.location.search).get('type');
    return TABS.some((t) => t.id === type) ? type : 'cost-groups';
  });
  const [highlightId, setHighlightId] = useState(
    () => new URLSearchParams(window.location.search).get('highlight') || null
  );
  const [uploads, setUploads] = useState([]);
  const [unlockRows, setUnlockRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuKey, setMenuKey] = useState(null);

  // Add-import modal
  const [addOpen, setAddOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [department, setDepartment] = useState('general');
  const [publishToBootcamp, setPublishToBootcamp] = useState(false);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState('');
  const fileInputRef = useRef(null);

  // Edit details modal
  const [editRow, setEditRow] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Bootcamp publish toggle (per card)
  const [bootcampSavingId, setBootcampSavingId] = useState(null);

  // Unlock tab
  const [bootcampUnlockOn, setBootcampUnlockOn] = useState(true);
  const [unlockSettingsLoading, setUnlockSettingsLoading] = useState(false);
  const [unlockSettingsSaving, setUnlockSettingsSaving] = useState(false);

  // Event lines splitting the unlock submissions. The modal edits a draft:
  // { id? (editing), label, atMs (exact line time), atInput (datetime-local text) }.
  const [unlockLines, setUnlockLines] = useState([]);
  const [lineDraft, setLineDraft] = useState(null);
  const [lineSaving, setLineSaving] = useState(false);
  const [lineError, setLineError] = useState('');

  // Emoji picker — targets a group tab ({ kind: 'group', tabId }) or an import row ({ kind: 'item', row })
  const [groupEmojis, setGroupEmojis] = useState({});
  const [emojiTarget, setEmojiTarget] = useState(null);
  const [emojiSaving, setEmojiSaving] = useState(false);

  // New-version upload modal
  const [versionRow, setVersionRow] = useState(null);
  const [versionNumber, setVersionNumber] = useState('');
  const [versionNotes, setVersionNotes] = useState('');
  const [versionFile, setVersionFile] = useState(null);
  const [versionError, setVersionError] = useState('');
  const [versionUploading, setVersionUploading] = useState(false);

  // Version history modal
  const [historyRow, setHistoryRow] = useState(null);
  const [versions, setVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const active = TABS.find((t) => t.id === activeTab) || TABS[0];
  const isUnlockTab = activeTab === 'unlock-info';
  const emojiOf = (tabId) =>
    groupEmojis[tabId] || TABS.find((t) => t.id === tabId)?.emoji || '📄';
  const userName =
    currentUser?.displayName || currentUser?.email?.split('@')[0] || 'BBA Team';
  const fileKind = getFileKindForTab(activeTab);
  const isJsonTab = fileKind === 'json';
  const fileExtUpper = isJsonTab ? 'JSON' : 'CSV';

  const card = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800';
  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const panel = `rounded-xl p-3.5 bg-gray-50 dark:bg-gray-800/60`;
  const chipGood =
    'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  const chipNeutral =
    'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  const iconBtn = `p-2 rounded-md border transition-colors ${
    darkMode
      ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white'
      : 'border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
  }`;

  const inputClass = `w-full rounded-xl border px-3 py-2 text-sm ${
    darkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;

  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;

  useEffect(() => {
    setMenuKey(null);
    setError(null);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'unlock-info') return undefined; // streamed live below
    let cancelled = false;
    (async () => {
      setListLoading(true);
      setError(null);
      try {
        const rows = await listImportsByTab(activeTab);
        if (!cancelled) setUploads(rows);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setUploads([]);
          setError(e.message || 'Could not load data.');
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  // Unlock tab: submissions and event lines stream live, so the "above the
  // line" count ticks up while an event is running.
  useEffect(() => {
    if (activeTab !== 'unlock-info') return undefined;
    let cancelled = false;
    let rowsReady = false;
    let linesReady = false;
    setListLoading(true);
    setUploads([]);
    setUnlockSettingsLoading(true);
    getBootcampUnlockEnabledPublic()
      .then((enabled) => {
        if (!cancelled) setBootcampUnlockOn(!!enabled);
      })
      .catch((e) => console.error('Unlock setting load failed:', e))
      .finally(() => {
        if (!cancelled) setUnlockSettingsLoading(false);
      });
    const markReady = () => {
      if (!cancelled && rowsReady && linesReady) setListLoading(false);
    };
    const fail = (e) => {
      console.error(e);
      if (cancelled) return;
      setError(e.message || 'Could not load data.');
      setListLoading(false);
    };
    const unsubRows = subscribeBootcampUnlockLeads((rows) => {
      if (cancelled) return;
      setUnlockRows(rows);
      rowsReady = true;
      markReady();
    }, fail);
    const unsubLines = subscribeBootcampUnlockLines((lines) => {
      if (cancelled) return;
      setUnlockLines(lines);
      linesReady = true;
      markReady();
    }, fail);
    return () => {
      cancelled = true;
      unsubRows();
      unsubLines();
    };
  }, [activeTab]);

  // Scroll to and briefly highlight a deep-linked import once its tab loads.
  useEffect(() => {
    if (!highlightId || listLoading) return undefined;
    if (!uploads.some((r) => r.id === highlightId)) return undefined;
    const scroll = setTimeout(() => {
      document
        .querySelector(`[data-import-card="${CSS.escape(highlightId)}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    const clear = setTimeout(() => setHighlightId(null), 3500);
    return () => {
      clearTimeout(scroll);
      clearTimeout(clear);
    };
  }, [highlightId, listLoading, uploads]);

  // Unlock submissions and lines stream live; only the import cards need a refetch.
  const refreshList = async () => {
    if (activeTab === 'unlock-info') return;
    const rows = await listImportsByTab(activeTab);
    setUploads(rows);
  };

  useEffect(() => {
    getImportGroupEmojis().then(setGroupEmojis).catch(() => {});
  }, []);

  // ---- Emoji picking (group tab or import card) ----
  const pickEmoji = async (emoji) => {
    if (!emojiTarget) return;
    setEmojiSaving(true);
    setError(null);
    try {
      if (emojiTarget.kind === 'group') {
        await saveImportGroupEmoji(emojiTarget.tabId, emoji);
        setGroupEmojis((prev) => ({ ...prev, [emojiTarget.tabId]: emoji }));
      } else {
        await updateTemplatesImportEmoji({ docId: emojiTarget.row.id, emoji });
        setUploads((prev) =>
          prev.map((r) => (r.id === emojiTarget.row.id ? { ...r, emoji: emoji || null } : r))
        );
      }
      setEmojiTarget(null);
    } catch (err) {
      console.error('Emoji save failed:', err);
      setError(err.message || 'Could not save the emoji.');
    } finally {
      setEmojiSaving(false);
    }
  };

  // ---- Add import ----
  const openAdd = () => {
    setTitle('');
    setDescription('');
    setDepartment('general');
    setPublishToBootcamp(false);
    setFile(null);
    setAddError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setAddOpen(true);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid) {
      setAddError('You must be signed in to upload.');
      return;
    }
    if (!file) {
      setAddError(isJsonTab ? 'Choose a JSON file to upload.' : 'Choose a CSV file to upload.');
      return;
    }
    setSubmitting(true);
    setAddError('');
    try {
      await uploadTemplatesImportFile({
        tabId: activeTab,
        title,
        description,
        department,
        file,
        userId: currentUser.uid,
        authorName: userName,
        source: 'crm',
        skipBootcampMirror: !publishToBootcamp,
      });
      setAddOpen(false);
      await refreshList();
    } catch (err) {
      console.error(err);
      setAddError(err.message || 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Edit details ----
  const openEdit = (row) => {
    setEditRow(row);
    setEditTitle(row.title || row.fileName || '');
    setEditDescription(row.description || '');
  };

  const submitEdit = async () => {
    if (!editRow) return;
    const t = editTitle.trim();
    if (!t) return;
    setSavingEdit(true);
    setError(null);
    try {
      await updateTemplatesImportMetadata({
        docId: editRow.id,
        title: t,
        description: editDescription,
      });
      setEditRow(null);
      await refreshList();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Save failed.');
    } finally {
      setSavingEdit(false);
    }
  };

  // ---- Reorder (display order here and on the portal) ----
  const moveImport = async (row, direction) => {
    const idx = uploads.findIndex((r) => r.id === row.id);
    const target = idx + direction;
    if (idx === -1 || target < 0 || target >= uploads.length) return;
    const next = [...uploads];
    [next[idx], next[target]] = [next[target], next[idx]];
    setUploads(next.map((r, i) => ({ ...r, sortOrder: i })));
    try {
      await saveImportsOrder(next.map((r) => r.id));
    } catch (err) {
      console.error('Reorder failed:', err);
      await refreshList();
    }
  };

  // ---- Bootcamp publish toggle ----
  const toggleBootcamp = async (row) => {
    setBootcampSavingId(row.id);
    setError(null);
    try {
      const resultId = await setTemplatesImportBootcampPublished({
        docId: row.id,
        published: !row.bootcampImportId,
      });
      setUploads((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, bootcampImportId: resultId } : r))
      );
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not update Bootcamp publishing.');
    } finally {
      setBootcampSavingId(null);
    }
  };

  // ---- New version upload ----
  const openVersionModal = (row) => {
    setVersionRow(row);
    setVersionNumber(suggestNextVersion(row.version));
    setVersionNotes('');
    setVersionFile(null);
    setVersionError('');
  };

  const submitVersion = async () => {
    if (!versionRow) return;
    if (!versionFile || !versionNumber.trim()) {
      setVersionError('Select a file and enter a version number.');
      return;
    }
    setVersionUploading(true);
    setVersionError('');
    try {
      await uploadTemplatesImportVersion({
        docId: versionRow.id,
        file: versionFile,
        version: versionNumber,
        notes: versionNotes,
        user: { uid: currentUser?.uid, name: userName },
      });
      setVersionRow(null);
      await refreshList();
    } catch (err) {
      console.error('Version upload failed:', err);
      setVersionError(err.message || 'Upload failed. Please try again.');
    } finally {
      setVersionUploading(false);
    }
  };

  // ---- Version history ----
  const openHistory = async (row) => {
    setHistoryRow(row);
    setVersions([]);
    setHistoryLoading(true);
    try {
      setVersions(await fetchImportVersionHistory(row.id));
    } catch (err) {
      console.error('History load failed:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRestore = async (versionId) => {
    if (!historyRow) return;
    setHistoryLoading(true);
    try {
      await rollbackImportToVersion(historyRow.id, versionId, {
        uid: currentUser?.uid,
        name: userName,
      });
      setVersions(await fetchImportVersionHistory(historyRow.id));
      await refreshList();
    } catch (err) {
      console.error('Restore failed:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title || row.fileName}"? Its file and version history are removed.`)) return;
    setError(null);
    try {
      await deleteTemplatesImport({ docId: row.id, storagePath: row.storagePath });
      await refreshList();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Delete failed.');
    }
  };

  const deptName = (id) => DEPARTMENTS.find((d) => d.id === id)?.name || id;

  const handleDeleteUnlockLead = async (row) => {
    if (!window.confirm('Delete this unlock entry?')) return;
    setError(null);
    try {
      await deleteBootcampUnlockLead(row.id);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Delete failed.');
    }
  };

  // ---- Event lines (split the unlock submissions by event) ----
  const openLineModal = (draft) => {
    setLineError('');
    setLineDraft({ ...draft, atInput: toLocalInputValue(draft.atMs) });
  };
  const openNewLine = () => openLineModal({ label: '', atMs: Date.now() });
  // Sits just under the row, so that submission and everything newer is above the line.
  const openLineUnder = (row) => openLineModal({ label: '', atMs: tsMs(row.createdAt) - 1 });
  const openEditLine = (line) =>
    openLineModal({ id: line.id, label: line.label || '', atMs: tsMs(line.at) });

  const lineAtValid = !!lineDraft?.atInput && !Number.isNaN(new Date(lineDraft.atInput).getTime());

  const saveLine = async (e) => {
    e.preventDefault();
    if (!lineDraft || lineSaving) return;
    if (!lineAtValid) {
      setLineError('Pick a date and time for the line.');
      return;
    }
    setLineSaving(true);
    setLineError('');
    try {
      if (lineDraft.id) {
        await updateBootcampUnlockLine(lineDraft.id, {
          label: lineDraft.label,
          atMs: lineDraft.atMs,
        });
      } else {
        await addBootcampUnlockLine({
          label: lineDraft.label,
          atMs: lineDraft.atMs,
          user: { uid: currentUser?.uid, name: userName },
        });
      }
      setLineDraft(null);
    } catch (err) {
      console.error('Event line save failed:', err);
      setLineError(err.message || 'Could not save the line.');
    } finally {
      setLineSaving(false);
    }
  };

  const handleDeleteLine = async (line) => {
    const name = line.label ? `the “${line.label}” line` : 'this event line';
    if (!window.confirm(`Remove ${name}? Submissions are not affected.`)) return;
    setError(null);
    try {
      await deleteBootcampUnlockLine(line.id);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not remove the line.');
    }
  };

  const sortedLines = useMemo(
    () => [...unlockLines].sort((a, b) => tsMs(b.at) - tsMs(a.at)),
    [unlockLines]
  );
  const unlockTimeline = useMemo(
    () => buildUnlockTimeline(unlockRows, sortedLines),
    [unlockRows, sortedLines]
  );
  const latestLine = sortedLines[0] || null;
  const aboveLineCount = latestLine
    ? unlockRows.filter((r) => tsMs(r.createdAt) > tsMs(latestLine.at)).length
    : null;

  // Live preview for the modal: submissions between the draft line and the next newer line.
  const linePreview = useMemo(() => {
    if (!lineDraft) return null;
    const at = lineDraft.atMs;
    const newer = sortedLines.filter((l) => l.id !== lineDraft.id && tsMs(l.at) > at);
    const nextLine = newer[newer.length - 1] || null;
    const ceiling = nextLine ? tsMs(nextLine.at) : Infinity;
    const count = unlockRows.filter((r) => {
      const t = tsMs(r.createdAt);
      return t > at && t <= ceiling;
    }).length;
    return { count, nextLine };
  }, [lineDraft, sortedLines, unlockRows]);

  const handleToggleBootcampUnlock = async () => {
    if (!currentUser?.uid || unlockSettingsLoading || unlockSettingsSaving) return;
    const next = !bootcampUnlockOn;
    setUnlockSettingsSaving(true);
    setError(null);
    try {
      await setBootcampUnlockEnabled(next);
      setBootcampUnlockOn(next);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not update unlock setting.');
    } finally {
      setUnlockSettingsSaving(false);
    }
  };

  const versionKind = versionRow
    ? getFileKindForTab(versionRow.tabId ?? versionRow.importType)
    : 'csv';
  const versionExtUpper = versionKind === 'json' ? 'JSON' : 'CSV';

  const tableShell =
    darkMode
      ? 'border-gray-600 divide-gray-700 text-gray-100'
      : 'border-gray-200 divide-gray-200 text-gray-900';
  const tableHead = darkMode ? 'bg-gray-900/60 text-gray-300' : 'bg-gray-50 text-gray-700';
  const tableRow = darkMode ? 'hover:bg-gray-900/40' : 'hover:bg-gray-50';

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative w-full max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <BackButton onClick={() => navigate('/crm/templates')} />
        <h1 className="text-3xl font-bold tracking-tight flex-1 text-center text-gray-900 dark:text-white">
          Imports
        </h1>
        <div className="w-10" aria-hidden />
      </div>

      {/* Group tabs (collection-style pills; the smile button on the active pill picks its emoji) */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <div
              key={tab.id}
              className={`inline-flex items-center rounded-full text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-purple-600 text-white'
                  : darkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <button
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 ${isActive ? 'pr-1' : ''}`}
              >
                <span className="mr-1.5">{emojiOf(tab.id)}</span>
                {tab.label}
              </button>
              {isActive && (
                <button
                  type="button"
                  onClick={() => setEmojiTarget({ kind: 'group', tabId: tab.id })}
                  className="pl-1 pr-3 py-2 text-white/80 hover:text-white"
                  title="Change group emoji"
                >
                  <SmilePlus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className={`text-sm mb-6 ${subtle}`}>{active.blurb}</p>

      {error && (
        <div
          className={`mb-4 rounded-md border px-4 py-3 text-sm ${
            darkMode
              ? 'border-red-800 bg-red-950/50 text-red-200'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {error}
        </div>
      )}

      {listLoading ? (
        <div className="flex justify-center py-16">
          <BBALoader size={40} />
        </div>
      ) : isUnlockTab ? (
        <>
        {/* Quick stats: every submission, and everything since the latest event line */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-5">
          <CrmStatCard
            icon={Users}
            label="Total submissions"
            value={unlockRows.length}
            hint={
              sortedLines.length
                ? `${sortedLines.length} event line${sortedLines.length === 1 ? '' : 's'}`
                : 'All time'
            }
          />
          <CrmStatCard
            icon={ArrowUpToLine}
            label="Above the line"
            value={latestLine ? aboveLineCount : '—'}
            tone="purple"
            hint={
              latestLine
                ? `Since ${latestLine.label ? `“${latestLine.label}”` : 'the latest line'} · ${formatDateTime(latestLine.at)}`
                : 'Add an event line to start counting'
            }
          />
        </div>
        <div className={`${card} rounded-2xl shadow-sm p-5`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Submissions
            </h3>
            <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 sm:shrink-0">
              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Bootcamp lead gate
              </span>
              <span
                className={`text-xs font-semibold tabular-nums w-6 ${
                  bootcampUnlockOn
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-500'
                }`}
              >
                {unlockSettingsLoading ? '—' : bootcampUnlockOn ? 'On' : 'Off'}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={bootcampUnlockOn}
                disabled={unlockSettingsLoading || unlockSettingsSaving}
                onClick={handleToggleBootcampUnlock}
                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-800 ${
                  bootcampUnlockOn
                    ? 'bg-green-600'
                    : darkMode
                      ? 'bg-gray-600'
                      : 'bg-gray-300'
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <span
                  className={`pointer-events-none inline-block h-6 w-6 translate-x-0.5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    bootcampUnlockOn ? 'translate-x-5' : 'translate-x-0.5'
                  } mt-0.5`}
                />
              </button>
              <span className="hidden sm:block h-6 w-px bg-gray-200 dark:bg-gray-700" aria-hidden />
              <button
                type="button"
                onClick={openNewLine}
                className="inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 shadow-sm"
              >
                <SeparatorHorizontal className="w-4 h-4" /> Add event line
              </button>
            </div>
          </div>
          {unlockTimeline.length === 0 ? (
            <p className={`text-sm py-10 ${subtle}`}>
              No unlock submissions yet. They appear when someone completes the form on the
              JobTread Bootcamp page.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
              <table className={`min-w-full text-sm divide-y ${tableShell}`}>
                <thead className={tableHead}>
                  <tr>
                    <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Name</th>
                    <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Email</th>
                    <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Phone</th>
                    <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Submitted</th>
                    <th className="text-right font-semibold px-4 py-3 whitespace-nowrap w-32">
                      {/* actions */}
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {unlockTimeline.map((item) => {
                    if (item.kind === 'line') {
                      const { line, count } = item;
                      return (
                        <tr key={`line-${line.id}`} className="bg-purple-50/80 dark:bg-purple-950/30">
                          <td colSpan={5} className="px-3 sm:px-4 py-2.5">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <span
                                className="h-0.5 min-w-[1rem] flex-1 rounded-full bg-purple-300 dark:bg-purple-700"
                                aria-hidden
                              />
                              <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-purple-200 dark:border-purple-800 bg-white dark:bg-gray-900 px-3 py-1 text-xs shadow-sm">
                                <span className="max-w-[16rem] truncate font-semibold text-purple-700 dark:text-purple-300">
                                  {line.label || 'Event line'}
                                </span>
                                <span className="text-gray-500 dark:text-gray-400">
                                  {formatDateTime(line.at)}
                                </span>
                                <span
                                  className="inline-flex items-center gap-0.5 rounded-full bg-purple-100 dark:bg-purple-900/50 px-1.5 py-0.5 font-semibold tabular-nums text-purple-700 dark:text-purple-300"
                                  title={`${count} submission${count === 1 ? '' : 's'} above this line`}
                                >
                                  <ArrowUp className="h-3 w-3" />
                                  {count} above
                                </span>
                              </span>
                              <span
                                className="h-0.5 min-w-[1rem] flex-1 rounded-full bg-purple-300 dark:bg-purple-700"
                                aria-hidden
                              />
                              <button
                                type="button"
                                onClick={() => openEditLine(line)}
                                className="p-1.5 rounded-md text-purple-500 hover:text-purple-700 hover:bg-purple-100 dark:text-purple-400 dark:hover:text-purple-200 dark:hover:bg-purple-900/40"
                                title="Edit line"
                                aria-label="Edit event line"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteLine(line)}
                                className="p-1.5 rounded-md text-purple-500 hover:text-red-600 hover:bg-red-50 dark:text-purple-400 dark:hover:text-red-300 dark:hover:bg-red-950/40"
                                title="Remove line"
                                aria-label="Remove event line"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    const { row } = item;
                    return (
                      <tr key={row.id} className={tableRow}>
                        <td className="px-4 py-3 align-top font-medium text-gray-900 dark:text-white">
                          {row.name || '—'}
                        </td>
                        <td className="px-4 py-3 align-top break-all">
                          {row.email ? (
                            <a
                              href={`mailto:${row.email}`}
                              className="text-purple-600 dark:text-purple-400 hover:underline"
                            >
                              {row.email}
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          {row.phone ? (
                            <a
                              href={`tel:${String(row.phone).replace(/\D/g, '')}`}
                              className="text-purple-600 dark:text-purple-400 hover:underline"
                            >
                              {row.phone}
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {formatDateTime(row.createdAt) || '—'}
                        </td>
                        <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => openLineUnder(row)}
                              className="inline-flex items-center rounded-md border border-gray-200 dark:border-gray-700 p-1.5 text-gray-500 dark:text-gray-400 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700 dark:hover:border-purple-700 dark:hover:bg-purple-950/40 dark:hover:text-purple-300"
                              title="Draw an event line under this submission"
                              aria-label="Draw an event line under this submission"
                            >
                              <SeparatorHorizontal className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteUnlockLead(row)}
                              className="inline-flex items-center gap-1 rounded-md border border-red-300 dark:border-red-800 px-2 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"
                              aria-label="Delete row"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {uploads.map((row, rowIndex) => {
            const rowTab = row.tabId ?? row.importType;
            const rowKind = getFileKindFromRow(row);
            const rowExtUpper = rowKind === 'json' ? 'JSON' : 'CSV';
            const FileIcon =
              rowKind === 'json'
                ? rowTab === 'workflows'
                  ? GanttChart
                  : LayoutDashboard
                : FileSpreadsheet;
            const published = !!row.bootcampImportId;
            const menuOpen = menuKey === row.id;
            const bootcampSaving = bootcampSavingId === row.id;
            return (
              <div
                key={row.id}
                data-import-card={row.id}
                className={`${card} rounded-2xl shadow-sm p-5 flex flex-col ${
                  highlightId === row.id ? 'ring-2 ring-red-400 dark:ring-red-500' : ''
                }`}
              >
                {/* Header: emoji tile + title/description + kebab menu */}
                <div className="flex items-start gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => setEmojiTarget({ kind: 'item', row })}
                    title="Choose an emoji for this import"
                    className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                      darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {row.emoji ? (
                      <span className="text-2xl leading-none">{row.emoji}</span>
                    ) : (
                      <FileIcon
                        className={`h-6 w-6 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}
                      />
                    )}
                  </button>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white leading-tight">
                      {row.title || row.fileName}
                    </h3>
                    {row.description && (
                      <p className={`text-sm ${subtle} mt-0.5 line-clamp-2`}>{row.description}</p>
                    )}
                  </div>
                  <div className="relative shrink-0 flex items-center">
                    <button
                      type="button"
                      onClick={() => moveImport(row, -1)}
                      disabled={rowIndex === 0}
                      className={`p-1.5 rounded-md disabled:opacity-25 disabled:cursor-default ${
                        darkMode
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                      }`}
                      title="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveImport(row, 1)}
                      disabled={rowIndex === uploads.length - 1}
                      className={`p-1.5 rounded-md disabled:opacity-25 disabled:cursor-default ${
                        darkMode
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                      }`}
                      title="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setMenuKey(menuOpen ? null : row.id)}
                      className={`p-1.5 rounded-md ${
                        darkMode
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                      title="More options"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {menuOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setMenuKey(null)} />
                        <div
                          className={`absolute right-0 top-8 z-20 w-44 rounded-lg shadow-lg border py-1 ${
                            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setMenuKey(null);
                              openEdit(row);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                              darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit details
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMenuKey(null);
                              handleDelete(row);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                              darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'
                            }`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete import
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Import file — the file clients download from the portal */}
                <div className={panel}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${subtle}`}>
                      Import file
                    </span>
                    <span className={chipGood}>
                      <CheckCircle className="w-3 h-3" /> v{row.version || '1.0'}
                    </span>
                  </div>
                  <p className={`text-xs ${subtle} mb-3`}>
                    {rowExtUpper}
                    {formatSize(row.size) ? ` · ${formatSize(row.size)}` : ''} · {deptName(row.department)}
                    {formatCreatedAt(row.updatedAt || row.createdAt)
                      ? ` · Updated ${formatCreatedAt(row.updatedAt || row.createdAt)}`
                      : ''}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openVersionModal(row)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md text-white bg-purple-600 hover:bg-purple-700"
                    >
                      <Upload className="w-3.5 h-3.5" /> New version
                    </button>
                    {row.downloadURL && (
                      <button
                        type="button"
                        onClick={() =>
                          downloadRemoteFile(row.downloadURL, row.fileName || row.title || 'download')
                        }
                        className={iconBtn}
                        title={`Download ${rowExtUpper}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => openHistory(row)}
                      className={iconBtn}
                      title="Version history"
                    >
                      <History className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Bootcamp page — public mirror on /welcome/JTBootcamp */}
                <div className={`${panel} mt-3`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${subtle}`}>
                      Bootcamp page
                    </span>
                    {published ? (
                      <span className={chipGood}>
                        <Unlock className="w-3 h-3" /> Published
                      </span>
                    ) : (
                      <span className={chipNeutral}>Not published</span>
                    )}
                  </div>
                  <p className={`text-xs ${subtle} mb-3`}>
                    {published
                      ? 'Publicly downloadable on the JobTread Bootcamp page.'
                      : 'Portal-only. Publish to also share it on the public Bootcamp page.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => toggleBootcamp(row)}
                    disabled={bootcampSaving}
                    className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md transition-colors disabled:opacity-50 ${
                      published
                        ? darkMode
                          ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-100'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {bootcampSaving ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Unlock className="w-3.5 h-3.5" />
                    )}
                    {published ? 'Unpublish' : 'Publish to Bootcamp'}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Add import tile */}
          <button
            type="button"
            onClick={openAdd}
            className={`rounded-lg border-2 border-dashed p-5 flex flex-col items-center justify-center gap-2 min-h-[180px] transition-colors ${
              darkMode
                ? 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500'
                : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
            }`}
          >
            <Plus className="w-6 h-6" />
            <span className="text-sm font-medium">Add import</span>
          </button>
        </div>
      )}

      <div className="flex items-center justify-center gap-2 mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
        <Clock className="w-4 h-4 shrink-0" />
        <span className="text-xs">Processing pipelines for these imports can be added later.</span>
      </div>

      {/* Add import modal */}
      <Modal isOpen={addOpen} onClose={() => !submitting && setAddOpen(false)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Add import</h2>
        <p className={`text-sm ${subtle} mb-4`}>
          Adds a new {fileExtUpper} import to “{active.label}”.
        </p>
        <form onSubmit={submitAdd} className="space-y-4">
          <div>
            <label htmlFor="import-title" className={labelClass}>
              Title
            </label>
            <input
              id="import-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Q1 cost group rollup"
              className={inputClass}
              disabled={submitting}
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="import-description" className={labelClass}>
              Description
            </label>
            <textarea
              id="import-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={isJsonTab ? 'What this JSON is for' : 'What this CSV is for'}
              rows={2}
              className={inputClass}
              disabled={submitting}
            />
          </div>
          <div>
            <label htmlFor="import-department" className={labelClass}>
              Department
            </label>
            <select
              id="import-department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className={inputClass}
              disabled={submitting}
            >
              {DEPARTMENTS.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span className={labelClass}>{isJsonTab ? 'JSON file' : 'CSV file'}</span>
            <label
              className={`mt-1 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 transition-colors ${
                darkMode
                  ? 'border-gray-600 hover:border-purple-500/60 bg-gray-900/30'
                  : 'border-gray-300 hover:border-purple-400 bg-gray-50/50'
              } ${submitting ? 'opacity-50 pointer-events-none' : ''}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={isJsonTab ? '.json,application/json' : '.csv,text/csv'}
                className="sr-only"
                disabled={submitting}
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setAddError('');
                }}
              />
              <Upload className={`mb-2 h-7 w-7 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {file
                  ? file.name
                  : isJsonTab
                    ? 'Click to choose a .json file'
                    : 'Click to choose a .csv file'}
              </span>
              <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">Up to 15MB</span>
            </label>
          </div>
          <label
            className={`flex items-start gap-3 rounded-lg border p-3.5 cursor-pointer transition-colors ${
              darkMode
                ? 'border-gray-600 bg-gray-900/30 hover:border-purple-500/60'
                : 'border-gray-300 bg-gray-50/50 hover:border-purple-400'
            } ${submitting ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <button
              type="button"
              role="switch"
              aria-checked={publishToBootcamp}
              disabled={submitting}
              onClick={() => setPublishToBootcamp((v) => !v)}
              className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-800 ${
                publishToBootcamp
                  ? 'bg-green-600'
                  : darkMode
                    ? 'bg-gray-600'
                    : 'bg-gray-300'
              } disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <span
                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out mt-0.5 ${
                  publishToBootcamp ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900 dark:text-white">
                Also publish to the JobTread Bootcamp page
              </span>
              <span className={`block text-xs mt-0.5 ${subtle}`}>
                Off by default — portal-only. You can publish or unpublish any time from the card.
              </span>
            </span>
          </label>

          {addError && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {addError}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              disabled={submitting}
              className={`px-4 py-2 rounded-md text-sm ${
                darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !file}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" /> Upload
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit details modal */}
      <Modal isOpen={!!editRow} onClose={() => !savingEdit && setEditRow(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Edit details</h2>
        <label className={labelClass}>Title</label>
        <input
          type="text"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          className={`${inputClass} mb-4`}
          disabled={savingEdit}
        />
        <label className={labelClass}>Description</label>
        <textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          rows={4}
          className={`${inputClass} mb-4`}
          disabled={savingEdit}
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setEditRow(null)}
            disabled={savingEdit}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={submitEdit}
            disabled={savingEdit || !editTitle.trim()}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400"
          >
            {savingEdit ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Modal>

      {/* New version modal */}
      <Modal isOpen={!!versionRow} onClose={() => !versionUploading && setVersionRow(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
          Upload new version
        </h2>
        <p className={`text-sm mb-4 ${subtle}`}>
          {versionRow?.title || versionRow?.fileName}
        </p>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Version number <span className={`font-normal ${subtle}`}>(auto-suggested)</span>
        </label>
        <input
          type="text"
          value={versionNumber}
          onChange={(e) => setVersionNumber(e.target.value)}
          placeholder="e.g. 1.1, 2.0"
          disabled={versionUploading}
          className={`w-full px-3 py-2 mb-4 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
          }`}
        />

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Change notes (optional)
        </label>
        <textarea
          value={versionNotes}
          onChange={(e) => setVersionNotes(e.target.value)}
          rows={2}
          placeholder="What changed in this version?"
          disabled={versionUploading}
          className={`w-full px-3 py-2 mb-4 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
          }`}
        />

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {versionExtUpper} file
        </label>
        <input
          type="file"
          accept={versionKind === 'json' ? '.json,application/json' : '.csv,text/csv'}
          disabled={versionUploading}
          onChange={(e) => {
            setVersionFile(e.target.files?.[0] || null);
            setVersionError('');
          }}
          className={`w-full text-sm mb-2 ${subtle}`}
        />
        <p className={`text-xs mb-4 ${subtle}`}>
          {versionExtUpper} up to 15MB. The previous file stays available in the version history.
        </p>

        {versionError && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {versionError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={() => setVersionRow(null)}
            disabled={versionUploading}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={submitVersion}
            disabled={versionUploading || !versionFile || !versionNumber.trim()}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {versionUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </Modal>

      {/* Version history modal */}
      <Modal isOpen={!!historyRow} onClose={() => setHistoryRow(null)} maxWidth="2xl">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Version history</h2>
        <p className={`text-sm mb-4 ${subtle}`}>
          {historyRow?.title || historyRow?.fileName}
        </p>

        {historyLoading ? (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-8">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading…
          </div>
        ) : versions.length === 0 ? (
          <p className={`py-6 text-center text-sm ${subtle}`}>No version history yet.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {versions.map((v, idx) => (
              <div
                key={v.id}
                className={`flex items-center justify-between p-3 rounded-md ${
                  darkMode ? 'bg-gray-700/60' : 'bg-gray-50'
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      v{v.version}
                    </span>
                    {idx === 0 && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        Current
                      </span>
                    )}
                  </div>
                  <p className={`text-xs truncate ${subtle}`}>
                    {v.uploadedByName} · {formatCreatedAt(v.uploadedAt) || '—'}
                    {v.notes ? ` · ${v.notes}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  <button
                    onClick={() => downloadRemoteFile(v.downloadURL, v.fileName || 'download')}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                    title="Download this version"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  {idx !== 0 && (
                    <button
                      onClick={() => handleRestore(v.id)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-600 text-white hover:bg-purple-700"
                      title="Roll back to this version"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Restore
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={() => setHistoryRow(null)}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Event line modal (add / edit) */}
      <Modal isOpen={!!lineDraft} onClose={() => !lineSaving && setLineDraft(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
          {lineDraft?.id ? 'Edit event line' : 'Add event line'}
        </h2>
        <p className={`text-sm ${subtle} mb-4`}>
          Splits the submissions list — everything submitted after the line sits above it.
        </p>
        <form onSubmit={saveLine} className="space-y-4">
          <div>
            <label htmlFor="line-label" className={labelClass}>
              Event name
            </label>
            <input
              id="line-label"
              type="text"
              value={lineDraft?.label || ''}
              onChange={(e) => setLineDraft((d) => ({ ...d, label: e.target.value }))}
              placeholder="e.g. Dallas JobTread Bootcamp"
              maxLength={120}
              className={inputClass}
              disabled={lineSaving}
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="line-at" className={labelClass}>
              Draw the line at
            </label>
            <input
              id="line-at"
              type="datetime-local"
              value={lineDraft?.atInput || ''}
              onChange={(e) => {
                const atInput = e.target.value;
                const ms = new Date(atInput).getTime();
                setLineDraft((d) => ({
                  ...d,
                  atInput,
                  atMs: atInput && !Number.isNaN(ms) ? ms : d.atMs,
                }));
                setLineError('');
              }}
              className={`${inputClass} ${darkMode ? '[color-scheme:dark]' : ''}`}
              disabled={lineSaving}
            />
            {linePreview && lineAtValid && (
              <p className={`text-xs mt-1.5 ${subtle}`}>
                <span className="font-semibold text-purple-600 dark:text-purple-400">
                  {linePreview.count} submission{linePreview.count === 1 ? '' : 's'}
                </span>{' '}
                will sit above this line
                {linePreview.nextLine
                  ? ` (up to the “${linePreview.nextLine.label || 'Event line'}” line)`
                  : ''}
                .
              </p>
            )}
          </div>

          {lineError && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {lineError}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setLineDraft(null)}
              disabled={lineSaving}
              className={`px-4 py-2 rounded-md text-sm ${
                darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={lineSaving || !lineAtValid}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {lineSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <SeparatorHorizontal className="h-4 w-4" />
                  {lineDraft?.id ? 'Save line' : 'Add line'}
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Emoji picker (groups and import cards) */}
      <Modal isOpen={!!emojiTarget} onClose={() => !emojiSaving && setEmojiTarget(null)} maxWidth="xl" zIndex={100}>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">
          Choose an emoji
        </h2>
        <p className={`text-sm mb-4 text-center ${subtle}`}>
          {emojiTarget?.kind === 'group'
            ? `${TABS.find((t) => t.id === emojiTarget.tabId)?.label || ''} group`
            : emojiTarget?.row?.title || emojiTarget?.row?.fileName}
        </p>
        <div className="flex justify-center">
          {emojiTarget && (
            <React.Suspense
              fallback={
                <div className="flex justify-center items-center h-72 w-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                </div>
              }
            >
              <EmojiPicker
                onEmojiClick={(emojiData) => !emojiSaving && pickEmoji(emojiData.emoji)}
                theme={darkMode ? 'dark' : 'light'}
                width="100%"
                height={380}
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
              />
            </React.Suspense>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          {(emojiTarget?.kind === 'group'
            ? !!groupEmojis[emojiTarget.tabId]
            : !!emojiTarget?.row?.emoji) ? (
            <button
              onClick={() => pickEmoji('')}
              disabled={emojiSaving}
              className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              Use default
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={() => setEmojiTarget(null)}
            disabled={emojiSaving}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {emojiSaving ? 'Saving…' : 'Cancel'}
          </button>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default TemplatesImports;
